<template>
  <div class="message-wrapper">
    <ResendMessageButton
      style="vertical-align: middle"
      v-if="getMessageStatus(message) === 'error'"
      :resendMessage="resendMessage"
      errorMessage="Resend message"
      :hide="shouldHideResendButton"
    />
    <div
      style="font-size: 0.875em; display: inline-block"
      slot="reference"
      :class="{
        'message-bubble-left': isUserMessage,
        'message-bubble-right': !isUserMessage,
      }"
    >
      <PreviewReply @updatePulseRowKey="$emit('updatePulseRowKey', $event)" :message="message" />
      <div
        v-if="message.data.content[0].images"
        v-viewer="$options.viewerOptions"
        style="cursor: pointer"
      >
        <div v-for="(item, index) in fileUrls" :key="index">
          <el-popover
            :placement="isUserMessage ? 'left' : 'right'"
            trigger="hover"
            min-width="50"
            :disabled="item.errorOrDeleted || !enableDeleteAction"
          >
            <el-button
              type="primary"
              slot="reference"
              :class="{
                'image-attachment-button': true,
                'whatsapp-sticker-button': item.isWhatsappSticker,
              }"
            >
              <img
                v-if="item.url"
                :class="{ 'whatsapp-sticker-image': item.isWhatsappSticker }"
                :src="
                  item.url +
                  (isAzureStorage && containQuery(item.url) ? '&' + item.cacheTimestamp : '')
                "
                @error="setError(item)"
              />
              <span v-else>{{ item.caption }}</span>
            </el-button>
            <div style="display: flex; align-items: center; justify-content: center">
              <el-button
                :disabled="!item.url"
                type="danger"
                round
                size="mini"
                @click="deleteAttachment(item)"
                >Delete</el-button
              >
            </div>
          </el-popover>
        </div>
      </div>
      <div v-else-if="message.data.content[0].files" v-viewer="$options.viewerOptions">
        <div v-for="(item, index) in fileUrls" :key="index">
          <el-popover
            :placement="isUserMessage ? 'left' : 'right'"
            trigger="hover"
            min-width="50"
            :disabled="item.errorOrDeleted || !enableDeleteAction"
          >
            <el-button
              slot="reference"
              class="file-attachment-button"
            >
              <div v-if="item.url" class="file-attachment-wrapper">
                <el-tooltip
                  :content="fileNameFromURL(item.url)"
                  placement="top"
                  :disabled="fileNameFromURL(item.url).length < 40"
                >
                  <a
                    :href="item.url + (isAzureStorage ? '&' + item.cacheTimestamp : '')"
                    target="_blank"
                    class="file-attachment-link"
                    :aria-label="'Download file: ' + fileNameFromURL(item.url)"
                  >
                    <div class="file-attachment-content">
                      <i
                        class="el-icon-document file-icon"
                        aria-hidden="true"
                      />
                      <span class="file-name">
                        {{ truncateFilename(fileNameFromURL(item.url)) }}
                      </span>
                    </div>
                  </a>
                </el-tooltip>
              </div>
              <span v-else>{{ item.caption }}</span>
            </el-button>
            <div style="display: flex; align-items: center; justify-content: center">
              <el-button
                :disabled="!item.url"
                type="danger"
                round
                size="mini"
                @click="deleteAttachment(item)"
                >Delete</el-button
              >
            </div>
          </el-popover>
        </div>
      </div>
      <div
        v-else-if="message.data.content[0].videos"
        v-viewer="$options.viewerOptions"
        style="cursor: pointer"
      >
        <div v-for="(item, index) in fileUrls" :key="index">
          <el-popover
            :placement="isUserMessage ? 'left' : 'right'"
            trigger="hover"
            min-width="50"
            :disabled="item.errorOrDeleted || !enableDeleteAction"
          >
            <el-button
              slot="reference"
              class="file-attachment-button"
            >
              <div v-if="item.url" class="file-attachment-wrapper">
                <el-tooltip
                  :content="fileNameFromURL(item.url)"
                  placement="top"
                  :disabled="fileNameFromURL(item.url).length < 40"
                >
                  <a
                    :href="item.url + (isAzureStorage ? '&' + item.cacheTimestamp : '')"
                    target="_blank"
                    class="file-attachment-link"
                    :aria-label="'Download video: ' + fileNameFromURL(item.url)"
                  >
                    <div class="file-attachment-content">
                      <i
                        class="el-icon-video-camera file-icon"
                        aria-hidden="true"
                      />
                      <span class="file-name">
                        {{ truncateFilename(fileNameFromURL(item.url)) }}
                      </span>
                    </div>
                  </a>
                </el-tooltip>
              </div>
              <span v-else>{{ item.caption }}</span>
            </el-button>
            <div style="display: flex; align-items: center; justify-content: center">
              <el-button
                :disabled="!item.url"
                type="danger"
                round
                size="mini"
                @click="deleteAttachment(item)"
                >Delete</el-button
              >
            </div>
          </el-popover>
        </div>
      </div>

      <div style="margin-bottom: 8px"></div> <!-- spacer between text and sender & timestamp -->
      <template v-if="sender">
        <div style="display:flex; justify-content: end; padding: 0 5px 0 5px">
          <strong style="text-transform: capitalize">{{ sender }}&nbsp;</strong>
        </div>
      </template>
      <div style="display: flex; justify-content: end">
        <div style="font-size: 9px; display: flex; align-items: center">
          {{ messageTimestamp(message.Timestamp || message.date_created) }}
          <i v-if="message.type !== 'message'" style="font-size: 13px; margin-left: 4px">
            <DeliveryIcon :status="message.status" />
          </i>
        </div>
      </div>
      <slot></slot>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import _ from "lodash";
import "viewerjs/dist/viewer.css";
import PreviewReply from "../PreviewReply";
import DeliveryIcon from "./DeliveryIcon";
import { MessageStatus } from "@/store/modules/livechat/types";
import ResendMessageButton from "./ResendMessageButton";

import { mapGetters } from "vuex";

export default {
  name: "File",
  components: {
    PreviewReply,
    DeliveryIcon,
    ResendMessageButton,
  },
  props: ["isUserMessage", "message", "buttonOutline", "hasMoreThanOneAgentInChat"],
  viewerOptions: {
    inline: false,
    button: true,
    navbar: true,
    title: true,
    toolbar: true,
    tooltip: true,
    movable: false,
    zoomable: true,
    rotatable: false,
    scalable: false,
    transition: false,
    fullscreen: false,
    keyboard: true,
  },
  data() {
    return {
      left: 0,
      active: false,
      fileUrls: [],
    };
  },
  mounted() {
    // fetch file url from webchat_request_file_agent endpoint
    // Reason: Allow viewing file for agents and sender only
    this.fetchFileUrl(this.message);
  },
  computed: {
    ...mapGetters(["isAzureStorage", "isQueueTab"]),
    enableDeleteAction() {
      return _.get(
        this,
        "$store.state.modules.handover.messageActions.enableAttachmentDeleteAction",
        false
      );
    },
    /**
     * @description Message sender name
     * @return {string}
     */
    sender() {
      if (!this.hasMoreThanOneAgentInChat || this.isUserMessage) {
        return null;
      }

      return _.get(this.message, "meta.name", null);
    },
    /**
     * @description Should hide resend button
     * @return {boolean}
     */
    shouldHideResendButton() {
      return !this.isQueueTab;
    },
  },
  methods: {
    messageTimestamp(timestamp) {
      return moment(timestamp).local().calendar(moment(), {
        sameDay: "[Today at] h:mm A",
        nextDay: "[Tomorrow at] h:mm A",
        nextWeek: "[Next] dddd [at] h:mm A",
        lastDay: "[Yesterday at] h:mm A",
        lastWeek: "DD-MMM-YYYY [at] h:mm A",
        sameElse: "DD-MMM-YYYY [at] h:mm A",
      });
    },
    deleteAttachment(item) {
      // Prefer the original stored blob URL (which carries the uuid/filename
      // path) over the display url — when the read proxy is enabled `item.url`
      // is a proxy URL that no longer contains the blob path, so deleting by it
      // would target the wrong (or no) blob.
      const fileName = this.fileNameWithUuidFromUrl(item.originalUrl || item.url);
      const result = this.$store
        .dispatch("DELETE_FILES", { fileNames: [fileName] })
        .then((result) => {
          if (result.success) {
            this.$message({
              message: "Attachment has been deleted",
              type: "success",
            });
            this.refreshCache(item);
          }
        });
    },
    refreshCache(item) {
      const index = this.fileUrls.indexOf(item);
      if (index > -1) {
        this.fileUrls[index].cacheTimestamp = Date.now();
      }
    },
    setError(item) {
      const index = this.fileUrls.indexOf(item);
      if (index > -1) {
        this.fileUrls[index].errorOrDeleted = true;
        this.fileUrls[index].url =
          "https://via.placeholder.com/468x60?text=Image%20not%20found%20or%20deleted";
      }
    },
    /**
     * Get file UUID and Name from url
     * @param{string} url The SAS URL of a file eg: https://domain/path/uuid/filename.jpg?signature=...
     * @return{string} The path with file uuid and name: uuid/filename.jpg
     */
    fileNameWithUuidFromUrl(url) {
      if (!url) {
        return "";
      }
      // The blob path may live in the `blobName` query param (server file
      // references) or in the URL path (direct storage URLs). Prefer the query
      // param — the same precedence FETCH_FILE_URL uses — otherwise fall back
      // to the path. Never strip the query before checking it, or the blob path
      // is lost and the endpoint name is sent instead.
      const takeLastTwo = (path) => path.split("/").filter(Boolean).slice(-2).join("/");
      try {
        const parsed = new URL(url, window.location.origin);
        const fromQuery = parsed.searchParams.get("blobName");
        return takeLastTwo(fromQuery || parsed.pathname);
      } catch (error) {
        const queryIndex = url.indexOf("?");
        return takeLastTwo(queryIndex >= 0 ? url.slice(0, queryIndex) : url);
      }
    },
    fileNameFromURL(item) {
      if (!item) {
        return "";
      }
      // Prefer the `filename` query param (server proxy URLs carry it, since
      // the proxy URL's last path segment is the endpoint name). Fall back to
      // the last path segment for direct storage URLs.
      try {
        const parsed = new URL(item);
        const fromQuery = parsed.searchParams.get("filename");
        if (fromQuery) {
          return fromQuery;
        }
        const segment = parsed.pathname.split("/").pop();
        return segment ? decodeURIComponent(segment) : "";
      } catch (error) {
        return item.slice(item.lastIndexOf("/") + 1, item.lastIndexOf("?"));
      }
    },
    async fetchFileUrl(message) {
      const { files, videos, images } = _.get(message, "data.content[0]", {});
      const urls = _.compact(files || videos || images);

      try {
        const transformedUrls = _.map(urls, (item) => {
          const { url = item, mimeType = "", caption = "" } = item;
          return url;
        });

        const fileUrls = await this.$store.dispatch("FETCH_MULTIPLE_FILE", transformedUrls);
        this.fileUrls = fileUrls.map((url, index) => ({
          url,
          // Keep the original stored blob URL so delete can resolve the real
          // uuid/filename blob path even when `url` is a server proxy URL.
          originalUrl: transformedUrls[index],
          blobName: urls?.[index]?.blobName,
          mediaId: urls?.[index]?.mediaId,
          mimeType: urls?.[index]?.mimeType,
          name: urls?.[index]?.name,
          animated: urls?.[index]?.animated,
          cacheTimestamp: Date.now(),
          errorOrDeleted: false,
          caption: urls?.[index]?.caption,
          isWhatsappSticker: this.isWhatsappStickerFile(urls?.[index]),
        }));
      } catch (error) {
        this.$notify({
          type: "error",
          title: "Error fetching image/file",
          message: "Please inform adminstrator of this error.",
        });
      }
    },
    isWhatsappStickerFile(item) {
      if (!item || typeof item === "string") {
        return false;
      }

      const mimeType = String(item.mimeType || "").toLowerCase();
      const blobName = String(item.blobName || "").toLowerCase();
      const url = String(item.url || "").toLowerCase();

      return (
        item.isWhatsappSticker === true ||
        (mimeType.includes("image/webp") &&
          (Boolean(item.mediaId) ||
            blobName.includes("whatsapp-stickers/") ||
            url.includes("whatsapp-stickers/")))
      );
    },
    /**
     * @description Resend message
     * @return {void}
     */
    resendMessage: _.debounce(
      function () {
        return this.$store.dispatch("RESEND_MESSAGE", {
          messageId: this.message.RowKey,
          fileUrls: this.fileUrls.map(item => item.url),
        }).catch((error) => {
          this.$notify.error({
            title: "Cannot resend message",
            message:
              error?.message || "Please check the internet connection and refresh this page.",
            position: "bottom-right",
          });
        });
      },
      500,
      { leading: true }
    ),
    getMessageStatus(message) {
      if (message.status === MessageStatus.SENDING) return "sending";
      if (message.status === MessageStatus.ERROR || message.status === MessageStatus.FAILED) return "error";
      return "normal";
    },
    truncateFilename(filename, maxLength = 35) {
      if (!filename || filename.length <= maxLength) {
        return filename;
      }

      const extIndex = filename.lastIndexOf('.');
      const extension = extIndex > -1 ? filename.substring(extIndex) : '';
      const name = extIndex > -1 ? filename.substring(0, extIndex) : filename;

      const availableLength = maxLength - extension.length - 3; // -3 for "..."

      if (availableLength > 10) { // Ensure meaningful truncation
        return name.substring(0, availableLength) + '...' + extension;
      }

      // Fallback: just truncate from end
      return filename.substring(0, maxLength - 3) + '...';
    },
    containQuery(url) {
      return url.includes('?');
    }
  },
};
</script>
<style>
/* File attachment styles */
.file-attachment-button {
  white-space: normal;
  word-wrap: break-word;
  line-height: 20px;
  padding: 8px 12px;
}

.file-attachment-wrapper {
  max-width: 100%;
}

.file-attachment-link {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  text-decoration: none;
  color: inherit;
  transition: opacity 0.2s ease;
}

.file-attachment-link:hover {
  opacity: 0.8;
}

.file-attachment-link:focus {
  outline: 2px solid #0b93f6;
  outline-offset: 2px;
  border-radius: 4px;
}

.file-attachment-content {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
}

.file-icon {
  font-size: 40px;
  flex-shrink: 0;
  line-height: 1;
}

.file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 220px;
  font-size: 14px;
  line-height: 1.4;
  vertical-align: middle;
}

.whatsapp-sticker-image {
  display: block;
  width: 150px;
  height: 150px;
  object-fit: contain;
}

.el-button.image-attachment-button {
  background-color: white;
  border-color: white;
}

.el-button.image-attachment-button:hover {
  background-color: white;
  border-color: white;
}

.el-button.whatsapp-sticker-button {
  padding: 0 !important;
  background-color: transparent !important;
  border-color: transparent !important;
}

/* Responsive breakpoints */
@media (max-width: 768px) {
  .file-icon {
    font-size: 32px;
  }

  .file-name {
    max-width: 160px;
    font-size: 12px;
  }
}

@media (min-width: 1024px) {
  .file-name {
    max-width: 260px;
  }
}

/* Screen reader only text */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.keyreply-bubble-content {
  word-wrap: break-word;
  text-align: left;
  overflow-y: auto;
  max-height: 400px;
}

.keyreply-bubble {
  position: relative;
  max-width: 300px;
  min-width: 150px;
  padding: 0 8px;
  margin: 8px;
  margin-left: 16px;
  margin-right: 16px;
  border-radius: 8px;
  font-size: 14px;
  padding: 8px;
  display: block;
  overflow: visible;
  word-wrap: break-word;
}

.keyreply-bubble.left {
  float: left;
  /* overflow-y: scroll; */
  /* max-height: 400px; */
}

.keyreply-bubble.right {
  float: right;
  /* overflow-y: scroll; */
  /* max-height: 400px; */
}

.keyreply-bubble:before {
  content: " ";
  position: absolute;
  width: 0;
  height: 0;
  top: 0;
  bottom: auto;
  border: 11px solid;
}

.keyreply-bubble.right:before {
  right: -8px;
  left: auto;
  border-color: inherit;
}

.keyreply-bubble.left:before {
  left: -8px;
  right: auto;
  border-color: inherit;
}

/* Facebook Messenger */

.keyreply-bubble.left.facebook {
  background: #0084ff;
}

.keyreply-bubble.right.facebook {
  background: #f1f0f0;
  color: black;
}
</style>
